import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Save, Sparkles, Plus, Trash2, Phone, MessageCircle } from "lucide-react";

const SOCIAL_FIELDS = [
  ["instagram", "Instagram", "https://instagram.com/…"],
  ["facebook", "Facebook", "https://facebook.com/…"],
  ["tiktok", "TikTok", "https://tiktok.com/@…"],
  ["linkedin", "LinkedIn", "https://linkedin.com/company/…"],
];

export default function VendreContactFaq({ settings, reload }) {
  const [contact, setContact] = useState(null);
  const [faq, setFaq] = useState([]);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (settings?.shop) {
      setContact({ whatsapp: "", phone: "", instagram: "", facebook: "", tiktok: "", linkedin: "", ...(settings.shop.contact || {}) });
      setFaq(settings.shop.faq || []);
    }
  }, [settings]);
  if (!contact) return null;

  const setC = (k, v) => setContact((p) => ({ ...p, [k]: v }));
  const dirty = JSON.stringify(contact) !== JSON.stringify({ whatsapp: "", phone: "", instagram: "", facebook: "", tiktok: "", linkedin: "", ...(settings.shop.contact || {}) })
    || JSON.stringify(faq) !== JSON.stringify(settings.shop.faq || []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/vendor/settings", { shop: { contact, faq } });
      toast.success("Contact & FAQ enregistrés");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setSaving(false);
  };

  const generate = async () => {
    if (faq.length > 0 && !window.confirm("La FAQ actuelle sera remplacée par la proposition de l'IA. Continuer ?")) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/vendor/shop/faq/generate");
      setFaq(data.faq);
      toast.success(`${data.faq.length} questions générées — relisez et enregistrez.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur de génération"); }
    setGenerating(false);
  };

  const input = "w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:border-violet-500";
  const lbl = "text-xs font-semibold text-slate-700 mb-1.5 block";

  return (
    <>
      {/* Contact & réseaux */}
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="vendre-contact-card">
        <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">Contact & réseaux sociaux</div>
        <p className="text-xs text-slate-500 mb-4">Affichés sur votre page publique : boutons WhatsApp et Appeler + icônes réseaux dans le pied de page. Un champ vide = bouton masqué.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <span className={lbl}><MessageCircle className="h-3 w-3 inline mr-1" />N° WhatsApp</span>
            <input value={contact.whatsapp} onChange={(e) => setC("whatsapp", e.target.value)} placeholder="+33 6 12 34 56 78" className={input} data-testid="contact-whatsapp" />
          </div>
          <div>
            <span className={lbl}><Phone className="h-3 w-3 inline mr-1" />N° d'appel</span>
            <input value={contact.phone} onChange={(e) => setC("phone", e.target.value)} placeholder="01 23 45 67 89" className={input} data-testid="contact-phone" />
          </div>
          {SOCIAL_FIELDS.map(([k, label, ph]) => (
            <div key={k}>
              <span className={lbl}>{label}</span>
              <input value={contact[k]} onChange={(e) => setC(k, e.target.value)} placeholder={ph} className={input} data-testid={`contact-${k}`} />
            </div>
          ))}
        </div>
      </div>

      {/* FAQ */}
      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="vendre-faq-card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">FAQ de votre page</div>
          <button onClick={generate} disabled={generating} className="inline-flex items-center gap-1.5 px-3.5 h-9 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-xs font-bold hover:opacity-90 disabled:opacity-50" data-testid="faq-generate-btn">
            {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            {generating ? "Génération…" : "Générer avec l'IA"}
          </button>
        </div>
        <p className="text-xs text-slate-500 mb-4">L'IA rédige la FAQ à partir de vos données réelles : CGV, caution, âge minimum, tarifs, options.</p>
        {faq.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 py-6 text-center text-xs text-slate-400">Aucune question — générez avec l'IA ou ajoutez manuellement.</div>
        ) : (
          <div className="space-y-2">
            {faq.map((f, i) => (
              <div key={i} className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`faq-item-${i}`}>
                <div className="flex gap-2">
                  <input value={f.q} onChange={(e) => setFaq((p) => p.map((x, j) => (j === i ? { ...x, q: e.target.value } : x)))} placeholder="Question" className={`${input} font-semibold`} />
                  <button onClick={() => setFaq((p) => p.filter((_, j) => j !== i))} className="h-10 w-10 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 flex items-center justify-center shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
                <textarea value={f.a} onChange={(e) => setFaq((p) => p.map((x, j) => (j === i ? { ...x, a: e.target.value } : x)))} placeholder="Réponse" rows={2} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-violet-500 resize-y" />
              </div>
            ))}
          </div>
        )}
        <button onClick={() => setFaq((p) => [...p, { q: "", a: "" }])} className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-violet-700 hover:text-violet-900" data-testid="faq-add-btn">
          <Plus className="h-3.5 w-3.5" /> Ajouter une question
        </button>
      </div>

      <div className="mt-4 flex justify-end">
        <button onClick={save} disabled={!dirty || saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-40 transition-colors" data-testid="vendre-contact-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer contact & FAQ
        </button>
      </div>
    </>
  );
}
